import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  BadgeCheck,
  Factory,
  ShieldCheck,
  Globe2,
  Sparkles,
  FlaskConical,
  Boxes,
  Leaf,
  Play,
  Quote,
} from "lucide-react";

// OPTIONAL: Replace with your real assets (recommended)
// import heroImg from "../assets/about/hero.jpg";
// import spicesThumb from "../assets/about/spices-thumb.jpg";
// import tagsImg from "../assets/about/tags.jpg";

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

export default function About() {
  return (
    <div className="bg-white">
      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0B12]">
        {/* Premium gradient background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(1100px_circle_at_15%_20%,rgba(244,124,32,0.22),transparent_60%),radial-gradient(1000px_circle_at_85%_15%,rgba(232,62,111,0.20),transparent_55%),radial-gradient(1100px_circle_at_60%_90%,rgba(201,162,39,0.14),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(0,0,0,0.78)_100%)]" />
          {/* subtle noise */}
          <div
            className="absolute inset-0 opacity-[0.06] mix-blend-soft-light"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.25'/%3E%3C/svg%3E\")",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* LEFT */}
            <motion.div
              variants={reveal}
              initial="hidden"
              animate="show"
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 backdrop-blur">
                <Sparkles size={16} className="text-[#F47C20]" />
                <span className="text-xs font-semibold tracking-[0.22em] uppercase text-white/70">
                  About Amera Exports
                </span>
              </div>

              <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
                Amera Exports{" "}
                <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
                  Private Limited
                </span>
              </h1>

              <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
                At Amera Export, we specialize in premium contract manufacturing
                for leading dietary supplement brands across the USA and Europe.
                Backed by rigorous quality control and industry certifications,
                we help brands bring their vision to life with customized
                solutions, cutting-edge formulations, and seamless production.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Pill icon={<BadgeCheck size={16} />} text="CGMP & HACCP aligned" />
                <Pill icon={<Globe2 size={16} />} text="Trusted in USA & Europe" />
                <Pill icon={<ShieldCheck size={16} />} text="Purity • Potency • Reliability" />
                <Pill icon={<Factory size={16} />} text="Contract Manufacturing" />
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <motion.a
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium text-white
                             bg-gradient-to-r from-[#F47C20] to-[#E83E6F] shadow-lg shadow-black/30 hover:opacity-95 transition"
                >
                  Request a Quote <ArrowUpRight size={18} className="ml-2" />
                </motion.a>

                <motion.a
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  href="/products"
                  className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium
                             border border-white/15 bg-white/5 text-white/85 hover:bg-white/10 transition backdrop-blur"
                >
                  Explore Products
                </motion.a>
              </div>
            </motion.div>

            {/* RIGHT: “model-like” visual (no three.js needed) */}
            <div className="lg:col-span-5 relative min-h-[360px]">
              <FloatingOrbs />
              <motion.div
                variants={reveal}
                initial="hidden"
                animate="show"
                className="relative rounded-[26px] border border-white/12 bg-white/6 backdrop-blur-xl p-6 shadow-[0_22px_90px_rgba(0,0,0,0.50)]"
              >
                <div className="h-[4px] w-full rounded-full bg-gradient-to-r from-[#F47C20] via-[#C9A227] to-[#E83E6F]" />
                <h3 className="mt-5 text-xl font-semibold text-white">
                  Largest exporter of Shilajit from India
                </h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Our expertise spans sourcing, processing, and manufacturing—ensuring
                  every product meets demanding global standards for purity and performance.
                </p>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  <MiniStat k="2019" v="Founded" />
                  <MiniStat k="100+" v="Markets" />
                  <MiniStat k="CGMP" v="Processes" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== STORY / TIMELINE ===================== */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#F47C20]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[#E83E6F]/10 blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#2B2B2B]/70">
              Precision. Purity. Partnership. Excellence.
            </p>

            <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-[#101018]">
              Our story — built on{" "}
              <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
                quality & trust
              </span>
            </h2>

            <p className="mt-4 text-base md:text-lg text-[#2B2B2B]/75 leading-relaxed">
              Amera Export began in 2019 with a small vision—to deliver world-class dietary
              supplements through precision-driven contract manufacturing. Today, we serve
              prominent brands across the USA and Europe with an export-ready ecosystem.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6"
          >
            <TimelineCard
              icon={<Factory size={18} />}
              title="State-of-the-art ecosystem"
              desc="A manufacturing setup designed for consistency, scalability, and global compliance."
              className="lg:col-span-4"
            />
            <TimelineCard
              icon={<FlaskConical size={18} />}
              title="Sourcing • Processing • Formulation"
              desc="From raw material selection to final formulation—every step is quality controlled."
              className="lg:col-span-4"
            />
            <TimelineCard
              icon={<Globe2 size={18} />}
              title="Global partner mindset"
              desc="We don’t just manufacture supplements—we help brands bring their vision to life."
              className="lg:col-span-4"
            />
          </motion.div>

          {/* Quote strip */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="mt-10 rounded-2xl border border-black/10 bg-gradient-to-r from-[#FFF7F0] to-white p-6 md:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] p-[1px] shrink-0">
                <div className="h-full w-full rounded-xl bg-white flex items-center justify-center">
                  <Quote size={18} className="text-[#C62828]" />
                </div>
              </div>
              <p className="text-base md:text-lg text-[#2B2B2B]/80 leading-relaxed">
                “Whether you’re a startup or an established leader, Amera Export is your trusted
                partner in delivering exceptional health & wellness products to the world.”
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== EXCELLENCE + MEDIA ===================== */}
      <section className="relative py-16 md:py-24 bg-[#0F1C24] overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(1000px_circle_at_20%_20%,rgba(244,124,32,0.18),transparent_60%),radial-gradient(900px_circle_at_85%_25%,rgba(232,62,111,0.16),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(0,0,0,0.70)_100%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="lg:col-span-6"
          >
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-white/60">
              Excellence in every dose
            </p>

            <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-white">
              Private labelling for{" "}
              <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
                global brands
              </span>
            </h2>

            <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed">
              We customize products to align with brand identity—formulation, packaging, and
              export-ready documentation. Quality remains the priority across every batch.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ValueChip icon={<ShieldCheck size={18} />} title="Rigorous QC" text="Testing at every stage for safety & compliance." />
              <ValueChip icon={<Boxes size={18} />} title="Brand-ready Packaging" text="Design support to match your positioning." />
              <ValueChip icon={<Leaf size={18} />} title="Sustainable Practices" text="Responsible sourcing & scalable processes." />
              <ValueChip icon={<BadgeCheck size={18} />} title="Certifications" text="Export-grade manufacturing standards." />
            </div>
          </motion.div>

          {/* Video thumbnail card (replace image later) */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="lg:col-span-6"
          >
            <div className="relative rounded-[26px] overflow-hidden border border-white/12 bg-white/6 backdrop-blur-xl shadow-[0_22px_90px_rgba(0,0,0,0.55)]">
              <div className="h-[4px] w-full bg-gradient-to-r from-[#F47C20] via-[#C9A227] to-[#E83E6F]" />

              {/* Placeholder media */}
              <div className="relative aspect-[16/10] bg-[radial-gradient(800px_circle_at_30%_30%,rgba(244,124,32,0.22),transparent_60%),radial-gradient(800px_circle_at_70%_40%,rgba(232,62,111,0.18),transparent_55%),linear-gradient(to_bottom,rgba(255,255,255,0.05),rgba(0,0,0,0.35))]">
                {/* If you have a real thumbnail image, use it:
                  <img src={spicesThumb} alt="Spices video" className="absolute inset-0 h-full w-full object-cover opacity-90" />
                */}

                <div className="absolute inset-0 bg-black/20" />

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                             h-16 w-16 rounded-full bg-white/85 text-[#C62828]
                             flex items-center justify-center shadow-xl"
                  aria-label="Play video"
                  type="button"
                >
                  <Play className="ml-1" />
                </motion.button>

                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-white/85 text-sm font-medium">
                    Tea spices with infuser • Manufacturing glimpse
                  </p>
                  <p className="mt-1 text-white/60 text-xs">
                    Quality systems • Packaging • Export readiness
                  </p>
                </div>
              </div>
            </div>

            {/* Small note */}
            <p className="mt-4 text-xs text-white/55">
              Replace the thumbnail with your real facility video preview anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#E83E6F]/12 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[#F47C20]/10 blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#FFF7F0] to-white p-10 md:p-12"
          >
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#2B2B2B]/70">
                  Request a Quote
                </p>
                <h3 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-[#101018]">
                  Tell us your needs, and we’ll send a custom quote.
                </h3>
                <p className="mt-3 text-base text-[#2B2B2B]/75 leading-relaxed">
                  From formulation to packaging & export documentation — we’ll recommend the most
                  efficient manufacturing route for your brand.
                </p>
              </div>

              <motion.a
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                href="/contact"
                className="inline-flex items-center justify-center rounded-xl px-7 py-4 text-sm font-medium text-white
                           bg-gradient-to-r from-[#F47C20] to-[#E83E6F] shadow-lg shadow-black/10 hover:opacity-95 transition"
              >
                CONTACT US <ArrowUpRight size={18} className="ml-2" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

/* ===================== Small Components ===================== */

function Pill({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 text-sm text-white/75 backdrop-blur">
      <span className="text-[#F47C20]">{icon}</span>
      {text}
    </span>
  );
}

function MiniStat({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded-xl border border-white/12 bg-white/5 px-4 py-3 text-center">
      <div className="text-sm font-semibold text-white">{k}</div>
      <div className="text-xs text-white/60 mt-0.5">{v}</div>
    </div>
  );
}

function TimelineCard({
  icon,
  title,
  desc,
  className = "",
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  className?: string;
}) {
  return (
    <motion.div
      variants={reveal}
      whileHover={{ y: -6 }}
      className={`rounded-2xl border border-black/10 bg-white p-6 shadow-sm hover:shadow-lg transition ${className}`}
    >
      <div className="h-12 w-12 rounded-xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] p-[1px]">
        <div className="h-full w-full rounded-xl bg-white flex items-center justify-center text-[#C62828]">
          {icon}
        </div>
      </div>
      <h3 className="mt-4 text-lg font-semibold text-[#101018]">{title}</h3>
      <p className="mt-2 text-sm text-[#2B2B2B]/75 leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function ValueChip({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="rounded-2xl border border-white/12 bg-white/5 backdrop-blur p-5"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] p-[1px] shrink-0">
          <div className="h-full w-full rounded-xl bg-[#0F1C24] flex items-center justify-center text-white/85">
            {icon}
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{title}</p>
          <p className="mt-1 text-sm text-white/65 leading-relaxed">{text}</p>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Floating “model-like” orbs (no three.js required)
 * Looks like animated premium 3D objects.
 */
function FloatingOrbs() {
  return (
    <div className="pointer-events-none absolute inset-0">
      <motion.div
        animate={{ y: [0, -18, 0], x: [0, 10, 0], rotate: [0, 6, 0] }}
        transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
        className="absolute -top-10 right-6 h-44 w-44 rounded-full blur-[1px]
                   bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.35),rgba(244,124,32,0.25),rgba(0,0,0,0)_70%)]"
      />
      <motion.div
        animate={{ y: [0, 16, 0], x: [0, -10, 0], rotate: [0, -6, 0] }}
        transition={{ repeat: Infinity, duration: 9.5, ease: "easeInOut" }}
        className="absolute bottom-0 left-6 h-56 w-56 rounded-full blur-[2px]
                   bg-[radial-gradient(circle_at_35%_35%,rgba(255,255,255,0.28),rgba(232,62,111,0.22),rgba(0,0,0,0)_72%)]"
      />
      <motion.div
        animate={{ y: [0, -10, 0], rotate: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 7.2, ease: "easeInOut" }}
        className="absolute top-24 left-28 h-28 w-28 rounded-full blur-[1px]
                   bg-[radial-gradient(circle_at_35%_35%,rgba(255,255,255,0.22),rgba(201,162,39,0.18),rgba(0,0,0,0)_70%)]"
      />

      {/* subtle dotted glow */}
      <div className="absolute inset-0 opacity-[0.12] bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:18px_18px]" />
    </div>
  );
}